import { ClassWithChildren } from "@helpers/class";
import { FiltersTypes } from "@common/types/filters/general";
import { Class } from "@common/types/class";

export interface ClassStatsMap {
    count: number;
}

export interface ClassRow {
    id: string;
    parentId: string | null;
    label: string;
    classInstance: ClassWithChildren | Class;
    depth: number;
    percentage: number;
    groupPercentage: number;
    transactionsCount: number;
    groupTransactionsCount: number;
    children?: ClassRow[];
}

interface BuildClassRowsOptions {
    search: string;
    filters: Pick<FiltersTypes, "classes">;
    statsMap: Map<string, ClassStatsMap>;
    total: number;
}

function matchesSearch(classItem: ClassWithChildren | Class, search: string) {
    if (search.length === 0) {
        return true;
    }
    return classItem.label.toLowerCase().includes(search.toLowerCase());
}

interface BuildClassRowParams {
    node: ClassWithChildren;
    parentId: string | null;
    /**
     * True when this node or one of its ancestors is the currently filtered
     * class, meaning the whole subtree below it should remain visible.
     */
    ancestorMatchedFilter: boolean;
    options: BuildClassRowsOptions;
}

/**
 * Recursively builds a `ClassRow` for `node`, returning `null` when neither the
 * node nor any descendant should be shown for the current search/filter.
 *
 * Search: a node is included when it matches the search term OR any descendant
 * matches (so the path to a match stays visible).
 *
 * Filter (`filters.classes[0]`): when a filter is set, only the filtered node,
 * its descendants, and its ancestors (to keep the path visible) are shown. In
 * practice the settings page only ever filters by a node that is rendered in
 * the tree, so at depth 2 (root + one child level) this is identical to the
 * previous root-scoped behavior.
 */
function buildClassRow({
    node,
    parentId,
    ancestorMatchedFilter,
    options,
}: BuildClassRowParams): ClassRow | null {
    const { search, filters, statsMap, total } = options;
    const filterId = filters.classes?.[0];

    const nodeMatchesFilter = !filterId || filterId === node.id;
    const inFilterSubtree =
        !filterId || ancestorMatchedFilter || nodeMatchesFilter;
    const childAncestorMatchedFilter =
        ancestorMatchedFilter || nodeMatchesFilter;

    const children = node.children
        .map((child) =>
            buildClassRow({
                node: child,
                parentId: node.id,
                ancestorMatchedFilter: childAncestorMatchedFilter,
                options,
            }),
        )
        .filter((row): row is ClassRow => row !== null);

    const matchesSearchSubtree =
        matchesSearch(node, search) || children.length > 0;

    // Keep this node when it (and its subtree) is part of the filtered subtree
    // and matches the search, or when a descendant is included (path to match).
    const shouldInclude =
        (inFilterSubtree && matchesSearchSubtree) || children.length > 0;
    if (!shouldInclude) {
        return null;
    }

    const count = statsMap.get(node.id)?.count ?? 0;
    // The backend only populates `groupCount` for root classes (non-roots get
    // 0), so subtree totals are computed client-side here: a node's subtree
    // count is its own direct count plus its children's already-computed subtree
    // counts. Because each child row carries its own subtree total, summing the
    // direct children is O(children); over the whole tree this is O(n)
    // (post-order, every node summed exactly once).
    const subtreeCount = children.reduce(
        (acc, child) => acc + child.groupTransactionsCount,
        count,
    );

    return {
        id: node.id,
        parentId,
        label: node.label,
        classInstance: node,
        depth: node.depth,
        percentage: total === 0 ? 0 : (count / total) * 100,
        groupPercentage: total === 0 ? 0 : (subtreeCount / total) * 100,
        transactionsCount: count,
        groupTransactionsCount: subtreeCount,
        children,
    };
}

export function buildClassRows(
    classesWithChildren: ClassWithChildren[],
    options: BuildClassRowsOptions,
): ClassRow[] {
    return classesWithChildren
        .map((node) =>
            buildClassRow({
                node,
                parentId: null,
                ancestorMatchedFilter: false,
                options,
            }),
        )
        .filter((row): row is ClassRow => row !== null);
}

/**
 * Build the expanded state with every row that has children expanded, at any
 * depth. At depth 2 only roots have children, so this matches the previous
 * top-level-only behavior.
 */
export function getClassDefaultExpandedState(
    rows: ClassRow[],
): Record<string, boolean> {
    const state: Record<string, boolean> = {};
    const walk = (nodes: ClassRow[]) => {
        for (const node of nodes) {
            if (node.children && node.children.length > 0) {
                state[node.id] = true;
                walk(node.children);
            }
        }
    };
    walk(rows);
    return state;
}

/**
 * Build a map of class id -> subtree transaction count (own direct count plus
 * every descendant's direct count), computed client-side because the backend
 * only populates `groupCount` for roots. Walking each root once visits every
 * node exactly once, so this is O(n).
 */
export function buildSubtreeCountMap(
    roots: ClassWithChildren[],
    directCountById: Map<string, number>,
): Map<string, number> {
    const subtreeCountById = new Map<string, number>();
    const walk = (node: ClassWithChildren): number => {
        const ownCount = directCountById.get(node.id) ?? 0;
        const subtreeCount = node.children.reduce(
            (acc, child) => acc + walk(child),
            ownCount,
        );
        subtreeCountById.set(node.id, subtreeCount);
        return subtreeCount;
    };
    roots.forEach(walk);
    return subtreeCountById;
}
